import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton, Button } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme imports to avoid lint warnings

const PAGE_ID = 'm6_u1_x1_konventionel';

const M6_U1_X1_Konventionel: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const images = [
    { key: 'motor_main', src: '/assets/drawable/motor_konventionel.png', label: 'Drehstrommotor mit Klemmbrett' },
    { key: 'klemmbrett_main', src: '/assets/drawable/klemmbrett_klemmstein_flansch_fuss_elektro_motor_js.png', label: 'Klemmbrett (offen)' },
    { key: 'motorklemmbrett_detail', src: '/assets/drawable/motorklembrett.png', label: 'Motorklemmbrett Detail' },
    { key: 'typenschild_230_400', src: '/assets/drawable/motortypenschild230400.png', label: 'Typenschild 230/400V' },
    { key: 'typenschild_400_690', src: '/assets/drawable/motortypenschild400690.png', label: 'Typenschild 400/690V' },
    { key: 'stern', src: '/assets/drawable/motor_stern.png', label: 'Sternschaltung (Diagramm)' },
    { key: 'dreieck', src: '/assets/drawable/motor_3eck.png', label: 'Dreieckschaltung (Diagramm)' },
    { key: 'stern_dreieck_plan', src: '/assets/drawable/motor_stern3eck.png', label: 'Stern‑Dreieck Schaltplan' },
    { key: 'dahlander', src: '/assets/drawable/motor_dahlander.png', label: 'Dahlander Motor (Polumschaltung)' }
  ];

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: 'Faszination Drehstrommotor', body: 'Während Gleichstrommotoren (DC) und herkömmliche 230V-Wechselstrommotoren in ihrer Ansteuerung oft überschaubar sind, eröffnet sich bei 400V-Drehstrommotoren eine Welt faszinierender Schaltungsvarianten mit direkten Auswirkungen auf Leistung, Drehzahl und Anlaufverhalten. Diese robusten Arbeitstiere der Industrie und des Handwerks verdanken ihre Flexibilität dem Aufbau ihres Herzstücks: dem Motorklemmbrett und der internen Verschaltung ihrer Wicklungen.' },
    { key: 'klemmbrett', heading: 'Das Klemmbrett: Das Tor zu den Wicklungen', body: 'Ein Blick auf das geöffnete Klemmbrett eines Standard-Drehstrommotors verrät sofort seine grundlegende Natur. Deutlich sichtbar sind die Anschlüsse für die drei im Motor verbauten Wicklungen: U1/U2 (erste Wicklung), V1/V2 (zweite Wicklung), W1/W2 (dritte Wicklung). Zusätzlich zu diesen sechs Leistungsanschlüssen findet man häufig auch separate Klemmen für einen Thermokontakt (TK) oder Kaltleiter (PTC). Dieser dient als wichtiger Schutzmechanismus, der den Motor vor Überhitzung bewahrt.' },
    { key: 'stern', heading: 'Sternschaltung (Y) – 400V-Betrieb', body: 'Ist auf dem Typenschild eine Spannungsangabe wie 230/400V (oder Δ/Y 230/400V) vermerkt, ist der Motor für den Betrieb in Sternschaltung am 400V-Netz vorgesehen. Die drei Phasen des Drehstromnetzes (L1, L2, L3) werden an die Anfänge der Wicklungen (U1, V1, W1) angeschlossen. Die Enden der Wicklungen (U2, V2, W2) werden miteinander verbunden. Dieser gemeinsame Verbindungspunkt wird als Sternpunkt bezeichnet. In der Sternschaltung liegt an jeder einzelnen Wicklung die Strangspannung an (ca. 230V bei 400V-Netz), was zu einem sanfteren Anlauf und geringerem Anlaufstrom führt.' },
    { key: 'dreieck', heading: 'Dreieckschaltung (Δ) – Alternative Verschaltung', body: 'Bei der Dreieckschaltung werden die Wicklungen so verschaltet, dass das Ende einer Wicklung mit dem Anfang der nächsten verbunden wird. Diese Schaltung wird oft bei Motoren verwendet, die für niedrigere Spannungen (z.B. 230V Dreieckschaltung) ausgelegt sind oder bei Stern-Dreieck-Anlauf, um den Motor nach dem Anlauf von Stern- auf Dreieckschaltung umzuschalten und so die volle Leistung zu erreichen.' },
    { key: 'anwendung', heading: 'Anwendung in der Praxis', body: 'Konventionelle Drehstrommotoren finden sich in zahlreichen Anwendungen: Ventilatoren, Pumpen, Kompressoren, Förderbänder. Die Wahl zwischen Stern- und Dreieckschaltung hängt von der Netzspannung und der gewünschten Leistung ab. Für Anwendungen mit hohen Anlaufströmen wird oft ein Stern-Dreieck-Anlauf verwendet, um die Belastung des Netzes zu reduzieren. Moderne Alternativen wie Frequenzumrichter oder EC-Motoren bieten zusätzliche Vorteile wie Drehzahlregelung und Energieeffizienz.' }
  ]), []);

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override, textBlocks]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.1.1 Konventionelle Motoren',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u1_x99_quiz') }, 'Zum Quiz')
    )
  ));
};

export default M6_U1_X1_Konventionel;


